import { useState } from "react";
import Page from "../Page";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Button from "../../components/input/button";
import useSubmitBugReport from "../../hooks/feedback/useSubmitBugReport";

const ReportABugPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [whatHappened, setWhatHappened] = useState("");
  const [whereWereYou, setWhereWereYou] = useState("");
  const [whatPlatform, setWhatPlatform] = useState("");
  const [didRefreshFix, setDidRefreshFix] = useState("no");
  const [otherComments, setOtherComments] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const { submit } = useSubmitBugReport();

  const handleSubmit = async () => {
    if (!whatHappened.trim()) {
      setSnackbarMessage(
        "You must at least describe what happened. The more information you can provide, the better chance we have of fixing the issue.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setSubmitting(true);

    const result = await submit({
      whatHappened,
      whereWereYou,
      whatPlatform,
      didRefreshFix: didRefreshFix === "true",
      otherComments,
    });

    setSubmitting(false);

    if (result.success) {
      setSnackbarMessage("Bug report submitted successfully. Thank you!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      // Clear form
      setWhatHappened("");
      setWhereWereYou("");
      setWhatPlatform("");
      setDidRefreshFix("no");
      setOtherComments("");
      // Navigate after a short delay to let user see the snackbar
      setTimeout(() => navigate("/feedback"), 1500);
    } else {
      setSnackbarMessage(`Failed to submit bug report. Please try again later`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <Page className="feedback-page" pageKey="feedback">
      <Typography variant="pageTitleWhite" className="feedback-page_title">
        Report a bug
      </Typography>

      <div className="feedback-page_panel">
        <Typography
          variant="formLabel"
          className="feedback-page_panel_input-label"
        >
          What happened?
        </Typography>
        <textarea
          className="feedback-page_panel_input-area"
          placeholder="Please describe in as much detail as possible what the bug is. What were you trying to do? What actually happened?"
          maxLength={500}
          value={whatHappened}
          onChange={(e) => setWhatHappened(e.target.value)}
        />

        <Typography
          variant="formLabel"
          className="feedback-page_panel_input-label"
        >
          Where were you?
        </Typography>
        <textarea
          className="feedback-page_panel_input-area"
          placeholder="Please describe the area of the site you were on when you found the bug. Which page were you on etc?"
          maxLength={500}
          value={whereWereYou}
          onChange={(e) => setWhereWereYou(e.target.value)}
        />

        <Typography
          variant="formLabel"
          className="feedback-page_panel_input-label"
        >
          What platform were you using?
        </Typography>
        <textarea
          className="feedback-page_panel_input-area"
          placeholder="Please give as much information as possible about your platform. Are you using a mobile or desktop device? Which browser are you using? What operating system are you using?"
          maxLength={500}
          value={whatPlatform}
          onChange={(e) => setWhatPlatform(e.target.value)}
        />

        <div>
          <FormControl>
            <Typography
              variant="formLabel"
              className="feedback-page_panel_input-label"
              component="label"
              id="demo-controlled-radio-buttons-group"
            >
              Did refreshing the page fix the issue?
            </Typography>
            <div className="feedback-page_panel_input-container">
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={didRefreshFix}
                onChange={(e) => setDidRefreshFix(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </div>
          </FormControl>
        </div>

        <Typography
          variant="formLabel"
          className="feedback-page_panel_input-label"
        >
          Any other comments?
        </Typography>
        <textarea
          className="feedback-page_panel_input-area"
          placeholder="Please provide any additional information you can give us that we may find useful"
          maxLength={500}
          value={otherComments}
          onChange={(e) => setOtherComments(e.target.value)}
        />

        <Button
          color="orange"
          text="Submit bug report"
          size="large"
          onClick={handleSubmit}
          loading={submitting}
          disabled={submitting}
        />
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Page>
  );
};

export default ReportABugPage;
